import {
  EnvVariablesProvider,
  UserManagementProvider,
} from '@innovamat/ga-features';
import { GlowApiClientProvider } from '@innovamat/glow-api-client';
import { storage } from '@innovamat/radiance-utils';
import { useRegionalConfigVariables } from '@innovamat/regional-config-variables';
import type { Credentials } from '@innovamat/social-login';
import { AuthProvider } from '@innovamat/social-login';
import {
  useWebviewBridge,
  WebviewBridgeProvider,
} from '@innovamat/webview-bridge';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { authServiceInit } from '../config/auth-service-init';
import '../config/i18n';
import { initEnvVariables } from '../config/init-env-variables';
import { queryClient } from '../config/query-client';
import Login from '../login/login';
import { useRedirectWithReferral } from '../utils/redirect-with-referral';
import {
  getAccessToken,
  getUserAcceptLanguage,
  updateRefreshTokenClient,
} from '../utils/update-refresh-token';
import Home from './home/home';
import { useUnityDataStore } from '../store/unity-data';

function AppStaticRouteRedirect(): JSX.Element {
  const { search } = useLocation();
  useEffect(() => {
    window.location.replace(`/app/index.html${search}`);
  }, [search]);
  return <></>;
}

const clientId = import.meta.env.VITE_USERS_CLIENT_ID_WEBAPP;

const initialCredentials = {
  apolloUrl: '',
  usersClientId: '',
  apiUsersUrl: '',
  msalClientId: '',
  googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
  googleSecretId: '',
  cleverClientId: '',
  cleverDistrictId: '',
  cleverSecret: '',
  rosterStreamAppId: import.meta.env.VITE_ROSTER_STREAM_APP_ID,
};

export default function AppRouter(): JSX.Element {
  const regionalConfigVariables = useRegionalConfigVariables();
  const { onRedirect } = useRedirectWithReferral();

  const { isInWebview } = useWebviewBridge();

  const { updateTokens } = useUnityDataStore();

  initialCredentials.usersClientId = isInWebview
    ? import.meta.env.VITE_USERS_CLIENT_ID_APP
    : import.meta.env.VITE_USERS_CLIENT_ID_WEBAPP;

  authServiceInit(
    regionalConfigVariables.apiUsers,
    initialCredentials.usersClientId
  );

  const [credentials, setCredentials] =
    useState<Credentials>(initialCredentials);

  useEffect(() => {
    setCredentials((prevCredentials) => ({
      ...prevCredentials,
      apolloUrl: regionalConfigVariables.apolloServer,
      apiUsersUrl: regionalConfigVariables.apiUsers,
    }));
  }, [regionalConfigVariables]);

  const handleRefreshToken = async (): Promise<void> => {
    const tokens = await updateRefreshTokenClient();
    updateTokens({
      AccessToken: tokens?.access_token,
      RefreshToken: tokens?.refresh_token,
      IdToken: tokens?.id_token,
      ClientID: clientId,
    });
  };

  if (credentials.apolloUrl === '' || credentials.apiUsersUrl === '') {
    return <></>;
  }

  return (
    <EnvVariablesProvider
      envVariables={initEnvVariables(regionalConfigVariables)}
    >
      <GlowApiClientProvider
        client={queryClient}
        apiUrl={regionalConfigVariables.apolloServer}
        getAuthToken={() => getAccessToken()}
        getUserAcceptLanguage={() => getUserAcceptLanguage()}
        onRefreshToken={handleRefreshToken}
        onRequestError={(error) => {
          if (error === 'UNAUTHENTICATED') {
            storage.clear();
            onRedirect();
          }
        }}
      >
        <AuthProvider credentials={credentials}>
          <UserManagementProvider studentJuniorLimitationEnabled={false}>
            <WebviewBridgeProvider>
              <Routes>
                <Route path="/app/*" element={<AppStaticRouteRedirect />} />
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/home" element={<Home />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </WebviewBridgeProvider>
          </UserManagementProvider>
        </AuthProvider>
      </GlowApiClientProvider>
    </EnvVariablesProvider>
  );
}
